/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import QsClassifierType from './QsClassifierType';
import Operator from './Operator';
import QuerySubjectUsage from './QuerySubjectUsage';
import MoserObject from './MoserObject';
import InstanceType from './InstanceType';
import QuerySubject from './QuerySubject';
import SetOperation from './SetOperation';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (runtime) // QsClassifierType
//@import com/ibm/bi/platform/moser/client/queryFlow/Operator (runtime) // Operator
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubjectUsage (runtime) // QuerySubjectUsage
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (runtime) // getModuleObject
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (runtime) // InstanceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubject (runtime) // new

/**
 * @author  NataliaBalaba
 */
export default class QueryFlowUtil extends BaseMoserObject{

	/**
	 * This method is helping to clean up a mess MUI created depicted in 293282
	 * @param {QuerySubject} querysubject
	 * @return {QsClassifierType}
	 */
	_getClassifier(querysubject) {
		if (querysubject.getClassifier()) {
			return querysubject.getClassifier();
		}
		return QsClassifierType.QUERY_SUBJECT;
	}

	/**
	 * Return type of query subject
	 * @param {QuerySubject} querysubject - query subject
	 * @return {Operator}  type of query subject
	 */
	getQuerySubjectType(querysubject) {
		if (!querysubject) {
			return null;
		}
		switch (this._getClassifier(querysubject)) {
		case QsClassifierType.JOIN_QUERY_SUBJECT:
			return Operator.JOIN;
		case QsClassifierType.OPERATION_QUERY_SUBJECT:
			var operation = querysubject.getQueryOperation().getSetOperation();
			switch (operation) {
			case SetOperation.EXCEPT:
				return Operator.EXCEPT;
			case SetOperation.INTERSECT:
				return Operator.INTERSECT;
			case SetOperation.UNION:
				return Operator.UNION;
			default:
			}
		case QsClassifierType.SQL_QUERY_SUBJECT:
			return Operator.SQL;
		default:
			return this.getPrecursors(querysubject).length==0 ? Operator.PLAIN : Operator.VIEW;
		}
	}

	/**
	 * Returns collection of selectable filters (if any) for the query subject
	 * @param {QuerySubject} querysubject - a query subject to compute selectable filters
	 * @return {Array}  collection of selectable filters (if any) for the query subject
	 */
	getSelectableFilters(querysubject) {
		var filters = [];
		this._appendFilters(querysubject.getItem(), filters);
		return filters;
	}

	_appendFilters(items, filters) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (item.getFilter()) {
				filters.push(item.getFilter());
			} else if (item.getFolder()) {
				this._appendFilters(item.getFolder().getItem(), filters);
			}
		}
	}

	/**
	 * Extract pre and post aggregate filters and place them in the respective collections
	 * @param {QuerySubject} querysubject - a query subject with the filters
	 * @param  preAggregate - empty list of pre aggregate filters to be filled with filters
	 * @param  postAggregate - empty list of post aggregate filters to be filled with filters
	 */
	extractFilters(querysubject) {
		var preAggregate = [], postAggregate = [];
		if (querysubject.basicGetFilter()) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querysubject.getFilter(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var filter = __exp_enFor0[__i_enFor0];
				if (filter.isPostAutoAggregation() != null && filter.isPostAutoAggregation() == true) {
					postAggregate.push(filter);
				} else {
					preAggregate.push(filter);
				}
			}
		}
		var result = [];
		result[0] = preAggregate;
		result[1] = postAggregate;
		return result;
	}

	/**
	 * @param {QuerySubject} querysubject
	 * @return {boolean}
	 */
	hasAggregate(querysubject) {
		return querysubject.getQuerySubjectUsage() == QuerySubjectUsage.SUMMARY_QUERY;
	}

	/**
	 * Return query subjects the query subject is based on - e.g. union legs, join participants, etc.
	 * @param {QuerySubject} querysubject
	 * @return {Array}
	 */
	getPrecursors(querysubject) {
		var module = MoserObject.getModuleObject(querysubject);
		if (!module) {
			return [];
		}
		switch (this._getClassifier(querysubject)) {
		case QsClassifierType.JOIN_QUERY_SUBJECT:
			return this._fromReference(querysubject.getJoinOperation().getQueryRef(), module);
		case QsClassifierType.OPERATION_QUERY_SUBJECT:
			return this._fromReference(querysubject.getQueryOperation().getQueryRef(), module);
		default:
			var instanceType = this._getInstanceType(querysubject);
			switch (instanceType) {
			case InstanceType.MISSING:
			case InstanceType.REFERENCE:
				return [];
			default:
				return this._fromReference(querysubject.getRef(), module);
			}
		}
	}

	_getInstanceType(querysubject) {
		if (!querysubject.getInstanceType()) {
			return InstanceType.COPY;
		}
		return querysubject.getInstanceType();
	}

	_fromReference(references, module) {
		var tables = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = references, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var ref = __exp_enFor0[__i_enFor0];
			if (ref.split(new RegExp("\\.")).length == 1) {
				var qs = this._getQuerySubjectById(ref, module);
				if (!qs) {
					qs = new QuerySubject();
					qs.setInstanceType(InstanceType.MISSING);
					qs.setIdentifier(ref);
				}
				tables.push(qs);
			}
		}
		return tables;
	}

	_getQuerySubjectById(id, module) {
		var querySubject;
		var qsList = module.basicGetQuerySubject();
		if (qsList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				if (qs.getIdentifier() == id) {
					querySubject = qs;
					break;
				}
			}
		}
		return querySubject;
	}
}


// WEBPACK FOOTER //
// ./src/generated/QueryFlowUtil.js